/** @format */

import { MouseEvent } from "react";
import styled from "styled-components";
import { colors, fonts, mixins } from "../../../../css/variables";
import { StatusLabel, Button } from "../../../ui-parts";
import { Box } from "../../../layouts/Box";

const WholeWrapperStyle = styled.div<{ status: string }>`
  background: ${(props) =>
    props.status === "CLOSED" ? colors.themeColor("background_disabled") : colors.themeColor("background_main")};
  border-radius: 12px;
  border: solid 1px ${colors.themeColor("border_box")};
  display: flex;
  padding: 20px 24px;
  justify-content: flex-start;
  box-shadow:
    0px 1px 2px rgba(0, 0, 0, 0.08),
    0px 0px 12px rgba(0, 0, 0, 0.08);
  position: relative;

  ${mixins.screenUnder()} {
    flex-direction: column;
    gap: 16px;
    padding: 16px;
  }
`;

const PlanLabel = styled.div<{ status: string }>`
  display: inline-block;
  background: ${(props) => (props.status === "CLOSED" ? colors.themeColor("plan_gray") : colors.themeColor("primary"))};
  color: ${colors.themeColor("text_white")};
  border-radius: 4px 4px 0 0;
  position: absolute;
  top: 0;
  left: 20px;
  padding: 2px 8px;
  transform: translate(0%, -100%);
`;

const LeftSection = styled.div`
  width: 110px;
  flex-shrink: 0;
  margin-right: 20px;
  position: relative;
  ${mixins.screenUnder()} {
    width: 100%;
    margin-right: 0;
  }
`;

const RightSection = styled.div`
  margin-left: auto;
  display: grid;
  align-items: end;
  ${mixins.screenUnder()} {
    margin-left: 0;
    justify-content: flex-start;
    border-top: 1px solid ${colors.themeColor("border_box")};
    padding-top: 16px;
  }
`;

const EntryIdLabel = styled.p`
  font-size: ${fonts.fontSizeRem("m")};
  color: ${colors.themeColor("text_gray")};
  margin-bottom: 0;
`;

const EntryIdValue = styled.p<{ status: string }>`
  font-size: ${fonts.fontSizeRem("xl")};
  color: ${(props) => (props.status === "CLOSED" ? colors.themeColor("text_gray") : colors.themeColor("text_main"))};
  font-weight: bold;
  margin-bottom: 8px;
`;

const CenterSection = styled.div`
  padding-left: 20px;
  border-left: 1px solid ${colors.themeColor("border_box")};
  ${mixins.screenUnder()} {
    border-left: none;
    border-top: 1px solid ${colors.themeColor("border_box")};
    padding-left: 0;
    padding-top: 16px;
  }
`;

const FieldLabel = styled.p<{ status: string }>`
  font-size: ${fonts.fontSizeRem("xxs")};
  color: ${(props) => (props.status === "CLOSED" ? colors.themeColor("text_gray") : colors.themeColor("text_main"))};
  margin-bottom: 0;
  font-weight: bold;
`;

const FieldValue = styled.p<{ status: string }>`
  font-size: ${fonts.fontSizeRem("xs")};
  color: ${(props) => (props.status === "CLOSED" ? colors.themeColor("text_gray") : colors.themeColor("text_main"))};
  margin-bottom: 0;
  word-break: break-all;
  overflow-wrap: break-word;
  max-width: 100%;
`;

const NotSetValue = styled.p`
  font-size: ${fonts.fontSizeRem("xs")};
  color: #9b9b9b;
  margin-bottom: 0;
`;

const IdStatusContainer = styled.div`
  display: block;

  ${mixins.screenUnder()} {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
  }
`;

const OcnkTeikiEntrySelectElement = ({
  entryData,
  entrySelectHandler,
}: {
  entryData: {
    entry_id: number;
    register_at: string | null;
    status: string;
    shop_name: string | null;
    shop_website_url: string | null;
    account_id: string | null;
    plan_name: string;
    close_date: string | null;
  };
  entrySelectHandler: (entryId: number) => void;
}) => {
  return (
    <WholeWrapperStyle status={entryData.status}>
      <>
        {entryData.plan_name && <PlanLabel status={entryData.status}>{entryData.plan_name}</PlanLabel>}
        <LeftSection>
          <IdStatusContainer>
            <div>
              <EntryIdLabel>申込ID</EntryIdLabel>
              <EntryIdValue status={entryData.status}>{entryData.entry_id}</EntryIdValue>
            </div>
            <StatusLabel status={entryData.status} />
          </IdStatusContainer>
        </LeftSection>

        <CenterSection>
          <FieldLabel status={entryData.status}>ご利用店舗名称</FieldLabel>
          {entryData.shop_name && entryData.shop_website_url ? (
            <>
              <FieldValue status={entryData.status}>{entryData.shop_name}</FieldValue>
              <FieldValue status={entryData.status}>{entryData.shop_website_url}</FieldValue>
            </>
          ) : (
            <NotSetValue>未設定</NotSetValue>
          )}
          <Box mt={12} />
          <FieldLabel status={entryData.status}>申込開始日</FieldLabel>
          <FieldValue status={entryData.status}>{entryData.register_at}</FieldValue>
          <Box mt={12} />
          <FieldLabel status={entryData.status}>アカウントID</FieldLabel>
          {entryData.account_id ? (
            <FieldValue status={entryData.status}>{entryData.account_id}</FieldValue>
          ) : (
            <NotSetValue>未設定</NotSetValue>
          )}
          <Box mt={12} />
          <FieldLabel status={entryData.status}>閲覧終了日</FieldLabel>
          {entryData.close_date ? (
            <FieldValue status={entryData.status}>{entryData.close_date}</FieldValue>
          ) : (
            <NotSetValue />
          )}
        </CenterSection>
        {entryData.status !== "CLOSED" && (
          <RightSection>
            <div>
              <Button
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  entrySelectHandler(entryData.entry_id);
                }}
                variant="secoundary"
                size="small"
              >
                ダッシュボードへ進む
              </Button>
            </div>
          </RightSection>
        )}
      </>
    </WholeWrapperStyle>
  );
};

export default OcnkTeikiEntrySelectElement;
